/**
 * Tiny wrapper around Sonner's `toast()` so the rest of the app can call
 * `notify.success(...)` without importing the runtime during SSR.
 *
 * Sonner is a client-only library (uses portals / requestAnimationFrame).
 * The wrapper is a no-op in `node` test or SSR contexts and lazily imports
 * the runtime on the client. This keeps the SSR bundle small and the
 * Vitest `node` environment happy.
 *
 * Sonner respects `dir="rtl"` on `<html>` automatically, so no extra
 * configuration is required for Arabic.
 */

type ToastFn = (message: string, opts?: { description?: string }) => void;

const noop: ToastFn = () => {
  // SSR / test no-op
};

export type Notify = {
  success: ToastFn;
  error: ToastFn;
  info: ToastFn;
};

let cached: Notify | null = null;

function loadClientNotify(): Notify {
  if (cached) return cached;
  if (typeof window === "undefined") {
    cached = { success: noop, error: noop, info: noop };
    return cached;
  }
  // Lazy import — keeps Sonner out of the SSR bundle.
  // Synchronous fallback (the import resolves on first call; until then,
  // calls are coalesced into the next tick).
  cached = { success: noop, error: noop, info: noop };
  void import("sonner").then((m) => {
    cached = {
      success: (msg, opts) => m.toast.success(msg, opts),
      error: (msg, opts) => m.toast.error(msg, opts),
      info: (msg, opts) => m.toast(msg, opts),
    };
  });
  return cached;
}

export const notify: Notify = {
  success: (msg, opts) => loadClientNotify().success(msg, opts),
  error: (msg, opts) => loadClientNotify().error(msg, opts),
  info: (msg, opts) => loadClientNotify().info(msg, opts),
};
